/**
 * Listing Detail Page
 *
 * Public listing view with contact form
 */

import { useParams, Link } from "react-router-dom";
import { useState } from "react";
import { useListing } from "@/lib/api/hooks";
import { ContactForm } from "@/components/listings/ContactForm";
import { ImageGallery } from "@/components/listings/ImageGallery";

export default function ListingDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data: listing, isLoading, error } = useListing(id || "");
  const [showContactForm, setShowContactForm] = useState(false);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-mist flex items-center justify-center">
        <div className="animate-pulse text-slate-500">Loading listing...</div>
      </div>
    );
  }

  if (error || !listing) {
    return (
      <div className="min-h-screen bg-mist flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-ink mb-2">Listing Not Found</h1>
          <p className="text-slate-600 mb-6">
            The listing you&apos;re looking for doesn&apos;t exist.
          </p>
          <Link to="/search" className="text-accent hover:text-accent-dark font-semibold">
            Back to Search
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-mist">
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Navigation */}
        <div className="mb-6">
          <Link to="/search" className="text-accent hover:text-accent-dark font-semibold">
            ← Back to Search
          </Link>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Image Gallery */}
            <div className="card overflow-hidden mb-8">
              <ImageGallery images={listing.mediaUrls} />
            </div>

            {/* Listing Info */}
            <div className="card p-8 mb-8">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h1 className="text-3xl font-bold text-ink mb-2">{listing.title}</h1>
                  <p className="text-slate-600 mb-4">
                    {listing.location} • {listing.year}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-3xl font-bold text-accent">
                    ${(listing.price / 100).toLocaleString()}
                  </p>
                  <p className="text-sm text-slate-600">{listing.currency}</p>
                </div>
              </div>

              {/* Specs */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 py-6 border-t border-b border-ash">
                <div>
                  <p className="text-sm text-slate-600">Type</p>
                  <p className="font-semibold text-ink capitalize">{listing.type}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-600">Length</p>
                  <p className="font-semibold text-ink">
                    {listing.length} {listing.lengthUnit}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-600">Make</p>
                  <p className="font-semibold text-ink">{listing.make}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-600">Model</p>
                  <p className="font-semibold text-ink">{listing.model}</p>
                </div>
              </div>

              {/* Package Info */}
              {listing.packageTier && (
                <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-600 font-semibold mb-1">
                    📌 {listing.packageTier.toUpperCase()} LISTING
                  </p>
                  <p className="text-sm text-blue-600">
                    Featured until{" "}
                    {listing.packageExpiresAt &&
                      new Date(listing.packageExpiresAt).toLocaleDateString()}
                  </p>
                </div>
              )}

              {/* Description */}
              <div className="mt-8">
                <h2 className="text-2xl font-bold text-ink mb-4">About This Boat</h2>
                <p className="text-slate-700 leading-relaxed whitespace-pre-wrap">
                  {listing.description}
                </p>
              </div>

              {/* Stats */}
              <div className="mt-8 pt-6 border-t border-ash">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-slate-600">Views</p>
                    <p className="text-2xl font-bold text-ink">{listing.views || 0}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">Inquiries</p>
                    <p className="text-2xl font-bold text-ink">{listing.inquiries || 0}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            {/* Seller Card */}
            <div className="card p-6 mb-6 sticky top-4">
              <h3 className="text-lg font-bold text-ink mb-4">Seller Info</h3>

              {listing.sellerId && (
                <div className="mb-6">
                  <p className="text-sm text-slate-600">Seller ID: {listing.sellerId}</p>
                </div>
              )}

              {!showContactForm ? (
                <button
                  onClick={() => setShowContactForm(true)}
                  className="w-full px-6 py-3 bg-accent text-white rounded-lg hover:bg-accent-dark transition font-semibold mb-3"
                >
                  Contact Seller
                </button>
              ) : (
                <button
                  onClick={() => setShowContactForm(false)}
                  className="w-full px-6 py-2 text-slate-600 hover:text-slate-700 text-sm mb-4"
                >
                  Hide Form
                </button>
              )}

              <a
                href="#"
                className="w-full block text-center px-6 py-3 border-2 border-accent text-accent rounded-lg hover:bg-blue-50 transition font-semibold"
              >
                Email Seller
              </a>
            </div>

            {/* Contact Form */}
            {showContactForm && (
              <div className="card p-6 mb-6">
                <h3 className="text-lg font-bold text-ink mb-4">Send a Message</h3>
                <ContactForm listingId={listing.id} />
              </div>
            )}

            {/* Share */}
            <div className="card p-6">
              <h3 className="text-lg font-bold text-ink mb-4">Share</h3>
              <div className="space-y-3">
                <button className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm">
                  Share on Facebook
                </button>
                <button className="w-full px-4 py-2 bg-sky-400 text-white rounded-lg hover:bg-sky-500 transition text-sm">
                  Share on Twitter
                </button>
                <button
                  onClick={() => {
                    if (navigator.share) {
                      navigator.share({
                        title: listing.title,
                        url: window.location.href,
                      });
                    }
                  }}
                  className="w-full px-4 py-2 border border-ash text-ink rounded-lg hover:bg-mist transition text-sm"
                >
                  Copy Link
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
